// ============================================================================
// HeroPassives.js - Hero Passive Definitions for ALL heroes
// ============================================================================
// Parsed from FS_ROV_Database Skills Sheet (Passive rows)
// 122 hero passives defined
// ============================================================================

import { 
  registerHeroPassive, 
  DamageType, 
  CCType, 
  BuffType, 
  TICKS_PER_SECOND, 
  applyHealing,
  applyBuff,
  calculateDamage,
} from './CombatEngine.js';

// ============================================================================
// PASSIVE DEFINITIONS
// Each passive has: onHit(actor, target, tick, log, triggerType), 
//                   onTick(actor, target, tick, log),
//                   onDamageTaken(defender, attacker, damage, tick, log)
// ============================================================================

const HERO_PASSIVES = {

  // ─── Airi: Swift ───
  // Every 5s, next normal attack hits twice (2nd deals 50%)
  'Airi': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      if (ps.internalCD <= 0) {
        // Double hit - 2nd deals 50%
        const stats = actor.totalStats;
        const bonusDmg = stats.phys_atk * 0.5;
        target.currentHP = Math.max(0, target.currentHP - bonusDmg);
        actor.totalDamageDealt += bonusDmg;
        target.totalDamageTaken += bonusDmg;
        ps.internalCD = 50; // 5s
        log.addEvent(tick, 'HERO_PASSIVE', {
          hero: actor.heroName, effect: 'Swift', detail: `Double hit! +${Math.round(bonusDmg)} dmg`,
        });
      }
    },
  },

  // ─── Aleister: Wicked Plot ───
  // 3 curse stacks → true damage
  'Aleister': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'SKILL') return;
      const ps = actor.heroPassiveState;
      ps.stacks = (ps.stacks || 0) + 1;
      if (ps.stacks >= 3) {
        const trueDmg = 100 + actor.level * 30 + (actor.totalStats.magic_power || 0) * 0.4;
        target.currentHP = Math.max(0, target.currentHP - trueDmg);
        actor.totalDamageDealt += trueDmg;
        target.totalDamageTaken += trueDmg;
        ps.stacks = 0;
        log.addEvent(tick, 'HERO_PASSIVE', {
          hero: actor.heroName, effect: 'Wicked Plot', detail: `True damage! ${Math.round(trueDmg)}`,
        });
      }
    },
  },

  // ─── Alice: Trot ───
  // Using abilities increases MS by 20% for 1s
  'Alice': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'SKILL') return;
      actor.buffs = actor.buffs.filter(b => b.source !== 'Trot');
      actor.buffs.push({
        type: BuffType.STAT_PCT, stat: 'movement_speed', value: 0.20,
        name: 'Trot', source: 'Trot', remainingTicks: 10,
      });
    },
  },

  // ─── Allain: Howling Sword ───
  // 7 hits / 4 phases, 7th hit = true damage, 8 stacks = heal
  'Allain': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      ps.stacks = (ps.stacks || 0) + 1;
      
      if (ps.stacks === 7) {
        const trueDmg = actor.totalStats.phys_atk * 0.6;
        target.currentHP = Math.max(0, target.currentHP - trueDmg);
        actor.totalDamageDealt += trueDmg;
        log.addEvent(tick, 'HERO_PASSIVE', {
          hero: actor.heroName, effect: 'Howling Sword', detail: `7th hit TRUE damage ${Math.round(trueDmg)}`,
        });
      }
      
      if (ps.stacks >= 8) {
        const heal = actor.maxHP * 0.06;
        applyHealing(actor, heal, log, tick, 'Howling Sword');
        ps.stacks = 0;
      }
    },
  },

  // ─── Amily: Duel ───
  // When only 1 enemy hero nearby: +20% damage, -20% damage taken
  'Amily': {
    onTick(actor, target, tick, log) {
      // In 1v1, always active
      const existing = actor.buffs.find(b => b.source === 'Duel');
      if (!existing) {
        actor.buffs.push({
          type: BuffType.DAMAGE_AMP, value: 0.20,
          name: 'Duel +DMG', source: 'Duel', remainingTicks: 15, permanent: false,
        });
        actor.buffs.push({
          type: BuffType.DAMAGE_REDUCTION, value: 0.20,
          name: 'Duel -DMG', source: 'Duel DR', remainingTicks: 15, permanent: false,
        });
      }
    },
  },

  // ─── Annette: Wind Force ───
  'Annette': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'SKILL') return;
      const ps = actor.heroPassiveState;
      if (ps.internalCD <= 0) {
        const bonusDmg = 100 + (actor.totalStats.magic_power || 0) * 0.3;
        target.currentHP = Math.max(0, target.currentHP - bonusDmg);
        actor.totalDamageDealt += bonusDmg;
        ps.internalCD = 50;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Wind Force', detail: `Bonus ${Math.round(bonusDmg)}` });
      }
    },
  },

  // ─── Aoi: Ninjutsu Shadow ───
  'Aoi': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      if (ps.customData.enhanced) {
        const bonusDmg = actor.totalStats.phys_atk * 0.5;
        target.currentHP = Math.max(0, target.currentHP - bonusDmg);
        actor.totalDamageDealt += bonusDmg;
        const heal = bonusDmg * 0.3;
        applyHealing(actor, heal, log, tick, 'Ninjutsu Shadow');
        ps.customData.enhanced = false;
      }
    },
    onSkillUse(actor) {
      actor.heroPassiveState.customData.enhanced = true;
    },
  },

  // ─── Arduin: Bloodlust ───
  'Arduin': {
    onTick(actor, target, tick, log) {
      if (actor.currentHP / actor.maxHP < 0.5) {
        const existing = actor.buffs.find(b => b.source === 'Bloodlust');
        if (!existing) {
          actor.buffs.push({
            type: BuffType.STAT_PCT, stat: 'attack_speed', value: 0.15,
            name: 'Bloodlust AS', source: 'Bloodlust', remainingTicks: 15,
          });
          actor.buffs.push({
            type: BuffType.STAT_FLAT, stat: 'life_steal', value: 0.10,
            name: 'Bloodlust LS', source: 'Bloodlust LS', remainingTicks: 15,
          });
        }
      }
    },
  },

  // ─── Arthur: Fervor ───
  'Arthur': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'SKILL') return;
      actor.buffs = actor.buffs.filter(b => b.source !== 'Fervor');
      actor.buffs.push({
        type: BuffType.STAT_PCT, stat: 'movement_speed', value: 0.10,
        name: 'Fervor', source: 'Fervor', remainingTicks: 20,
      });
    },
  },

  // ─── Arum: Ravenous Beasts ───
  'Arum': {
    onTick(actor, target, tick, log) {
      if (tick % 10 !== 0) return; // Every 1s
      const dmg = 50 + (actor.totalStats.magic_power || 0) * 0.15;
      target.currentHP = Math.max(0, target.currentHP - dmg);
      actor.totalDamageDealt += dmg;
      const heal = dmg * 0.5;
      applyHealing(actor, heal, log, tick, 'Ravenous Beasts');
    },
  },

  // ─── Astrid: Fearless ───
  'Astrid': {
    onDamageTaken(defender, attacker, damage, tick, log) {
      if (defender.currentHP / defender.maxHP < 0.3 && !defender.heroPassiveState.customData.shielded) {
        const shield = defender.maxHP * 0.15;
        defender.shields.push({ amount: shield, source: 'Fearless', remainingTicks: 30 });
        defender.heroPassiveState.customData.shielded = true;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: defender.heroName, effect: 'Fearless', detail: `Shield ${Math.round(shield)}` });
      }
    },
  },

  // ─── Azzen'Ka: Sand Trap ───
  "Azzen'Ka": {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'SKILL') return;
      const ps = actor.heroPassiveState;
      ps.stacks = (ps.stacks || 0) + 1;
      if (ps.stacks >= 3) {
        // Petrify 0.75s
        target.debuffs.push({
          type: BuffType.CC, ccType: CCType.PETRIFY, name: 'Sand Trap',
          source: 'Sand Trap', remainingTicks: 8,
        });
        ps.stacks = 0;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Sand Trap', detail: 'Petrify!' });
      }
    },
  },

  // ─── Baldum: Wild Charge ───
  'Baldum': {
    onTick(actor, target, tick, log) {
      const ps = actor.heroPassiveState;
      ps.stacks = Math.min((ps.stacks || 0) + 1, 100); // Build charge over time
    },
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      if (ps.stacks >= 100) {
        const bonusDmg = actor.totalStats.phys_atk * 0.8;
        target.currentHP = Math.max(0, target.currentHP - bonusDmg);
        actor.totalDamageDealt += bonusDmg;
        target.debuffs.push({ type: BuffType.CC, ccType: CCType.KNOCKUP, name: 'Wild Charge', source: 'Wild Charge', remainingTicks: 5 });
        ps.stacks = 0;
      }
    },
  },

  // ─── Bijan: Martial Arts ───
  'Bijan': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      ps.stacks = (ps.stacks || 0) + 1;
      if (ps.stacks >= 3) {
        const bonusDmg = actor.totalStats.phys_atk * 0.5;
        target.currentHP = Math.max(0, target.currentHP - bonusDmg);
        actor.totalDamageDealt += bonusDmg;
        ps.stacks = 0;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Martial Arts', detail: `3rd hit +${Math.round(bonusDmg)}` });
      }
    },
  },

  // ─── Butterfly: Assassin ───
  'Butterfly': {
    onHit(actor, target, tick, log, trigger) {
      // On kill/assist: stacks. 3 stacks = reset CD
      // In sim: simplified to on-kill
    },
  },

  // ─── Capheny: Pulse Shot ───
  'Capheny': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      ps.stacks = (ps.stacks || 0) + 1;
      if (ps.stacks >= 4) {
        const bonusDmg = actor.totalStats.phys_atk * 0.3;
        target.currentHP = Math.max(0, target.currentHP - bonusDmg);
        actor.totalDamageDealt += bonusDmg;
        ps.stacks = 0;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Pulse Shot', detail: `Bonus shots! +${Math.round(bonusDmg)}` });
      }
    },
  },

  // ─── Diaochan: Ice Queen ───
  'Diaochan': {
    onDamageTaken(defender, attacker, damage, tick, log) {
      const ps = defender.heroPassiveState;
      if (!ps.customData.shieldBroken && ps.internalCD <= 0) {
        defender.shields.push({ amount: damage * 1.5, source: 'Ice Queen', remainingTicks: 10 });
        defender.isInvulnerable = true;
        setTimeout(() => { defender.isInvulnerable = false; }, 1000);
        ps.customData.shieldBroken = true;
        ps.internalCD = 120 * TICKS_PER_SECOND;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: defender.heroName, effect: 'Ice Queen', detail: 'Shield activated!' });
      }
    },
  },

  // ─── Eland'orr: Eagle Eye ───
  "Eland'orr": {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      ps.stacks = Math.min((ps.stacks || 0) + 1, 5);
      const bonusDmg = actor.totalStats.phys_atk * 0.05 * ps.stacks;
      if (bonusDmg > 0) {
        target.currentHP = Math.max(0, target.currentHP - bonusDmg);
        actor.totalDamageDealt += bonusDmg;
      }
    },
  },

  // ─── Elsu: Rifled Barrel ───
  'Elsu': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      if (ps.internalCD <= 0) {
        const bonusDmg = actor.totalStats.phys_atk * 0.5;
        target.currentHP = Math.max(0, target.currentHP - bonusDmg);
        actor.totalDamageDealt += bonusDmg;
        ps.internalCD = 30; // Charge time
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Rifled Barrel', detail: `Charged shot +${Math.round(bonusDmg)}` });
      }
    },
  },

  // ─── Enzo: Backstab ───
  'Enzo': {
    onHit(actor, target, tick, log, trigger) {
      // Bonus true damage from behind (simplified: 20% chance in sim)
      if (Math.random() < 0.3) {
        const trueDmg = actor.totalStats.phys_atk * 0.3;
        target.currentHP = Math.max(0, target.currentHP - trueDmg);
        actor.totalDamageDealt += trueDmg;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Backstab', detail: `True damage +${Math.round(trueDmg)}` });
      }
    },
  },

  // ─── Errol: Blood Pact ───
  'Errol': {
    onHit(actor, target, tick, log, trigger) {
      const heal = actor.totalDamageDealt * 0.03; // Simplified
      if (heal > 0) {
        applyHealing(actor, Math.min(heal, 50), log, tick, 'Blood Pact');
      }
    },
  },

  // ─── Fennik: Thief's Mark ───
  'Fennik': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      target.debuffs = target.debuffs.filter(d => d.source !== "Thief's Mark");
      target.debuffs.push({
        type: BuffType.DAMAGE_REDUCTION, value: -0.10, // Negative = more damage taken
        name: "Thief's Mark", source: "Thief's Mark", remainingTicks: 30,
      });
    },
  },

  // ─── Florentino: Shuffle ───
  'Florentino': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      if (ps.customData.flowerAvailable) {
        const heal = actor.maxHP * 0.04;
        applyHealing(actor, heal, log, tick, 'Shuffle');
        const trueDmg = actor.totalStats.phys_atk * 0.3;
        target.currentHP = Math.max(0, target.currentHP - trueDmg);
        actor.totalDamageDealt += trueDmg;
        ps.customData.flowerAvailable = false;
      }
    },
    onSkillUse(actor) {
      actor.heroPassiveState.customData.flowerAvailable = true;
    },
  },

  // ─── Gildur: The Touch ───
  'Gildur': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      ps.stacks = (ps.stacks || 0) + 1;
      if (ps.stacks >= 4) {
        const bonusDmg = actor.totalStats.phys_atk * 1.5;
        target.currentHP = Math.max(0, target.currentHP - bonusDmg);
        target.debuffs.push({ type: BuffType.CC, ccType: CCType.KNOCKBACK, name: 'The Touch', source: 'The Touch', remainingTicks: 5 });
        actor.shields.push({ amount: actor.maxHP * 0.08, source: 'The Touch', remainingTicks: 30 });
        ps.stacks = 0;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'The Touch', detail: `4th hit knockback + ${Math.round(bonusDmg)} magic dmg` });
      }
    },
  },

  // ─── Grakk: Blood Ritual ───
  'Grakk': {
    onDamageTaken(defender, attacker, damage, tick, log) {
      if (damage >= defender.maxHP * 0.1 && defender.heroPassiveState.internalCD <= 0) {
        const explodeDmg = 200 + (defender.totalStats.magic_power || 0) * 0.4;
        attacker.currentHP = Math.max(0, attacker.currentHP - explodeDmg);
        defender.totalDamageDealt += explodeDmg;
        defender.heroPassiveState.internalCD = 20;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: defender.heroName, effect: 'Blood Ritual', detail: `Explode! ${Math.round(explodeDmg)}` });
      }
    },
  },

  // ─── Hayate: Shuriken Toss ───
  'Hayate': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const trueDmg = target.maxHP * 0.02;
      target.currentHP = Math.max(0, target.currentHP - trueDmg);
      actor.totalDamageDealt += trueDmg;
      target.totalDamageTaken += trueDmg;
      log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Shuriken Toss', detail: `2% max HP true: ${Math.round(trueDmg)}` });
    },
  },

  // ─── Ilumia: Goddess ───
  'Ilumia': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'SKILL') return;
      const ps = actor.heroPassiveState;
      ps.stacks = (ps.stacks || 0) + 1;
      if (ps.stacks >= 5) {
        const dmg = 200 + (actor.totalStats.magic_power || 0) * 0.5;
        target.currentHP = Math.max(0, target.currentHP - dmg);
        actor.totalDamageDealt += dmg;
        ps.stacks = 0;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Goddess', detail: `Divine Light ${Math.round(dmg)}` });
      }
    },
  },

  // ─── Kriknak: Shatter ───
  'Kriknak': {
    onHit(actor, target, tick, log, trigger) {
      const ps = actor.heroPassiveState;
      ps.customData.targetHits = (ps.customData.targetHits || 0) + 1;
      if (ps.customData.targetHits >= 3) {
        const bonusDmg = actor.totalStats.phys_atk * 0.4;
        target.currentHP = Math.max(0, target.currentHP - bonusDmg);
        actor.totalDamageDealt += bonusDmg;
        ps.customData.targetHits = 0;
      }
    },
  },

  // ─── Lauriel: Divine Punishment ───
  'Lauriel': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'SKILL') return;
      const ps = actor.heroPassiveState;
      ps.stacks = (ps.stacks || 0) + 1;
      if (ps.stacks >= 3) {
        const trueDmg = 180 + actor.level * 20 + (actor.totalStats.magic_power || 0) * 0.4;
        target.currentHP = Math.max(0, target.currentHP - trueDmg);
        actor.totalDamageDealt += trueDmg;
        ps.stacks = 0;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Divine Punishment', detail: `True damage ${Math.round(trueDmg)}` });
      }
    },
  },

  // ─── Laville: Double Trouble ───
  'Laville': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const bonusDmg = actor.totalStats.phys_atk * 0.5;
      target.currentHP = Math.max(0, target.currentHP - bonusDmg);
      actor.totalDamageDealt += bonusDmg;
    },
  },

  // ─── Lu Bu: Martial Master ───
  'Lu Bu': {
    onHit(actor, target, tick, log, trigger) {
      // Each hit reduces S1 CD by 1s
      if (actor.cooldowns['Skill 1'] && actor.cooldowns['Skill 1'] > 0) {
        actor.cooldowns['Skill 1'] = Math.max(0, actor.cooldowns['Skill 1'] - TICKS_PER_SECOND);
      }
    },
  },

  // ─── Maloch: Plunder ───
  'Maloch': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const trueDmg = 100 + actor.level * 10;
      target.currentHP = Math.max(0, target.currentHP - trueDmg);
      actor.totalDamageDealt += trueDmg;
      applyHealing(actor, trueDmg * 0.5, log, tick, 'Plunder');
      log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Plunder', detail: `True ${Math.round(trueDmg)} + heal` });
    },
  },

  // ─── Marja: Soul Steal ───
  'Marja': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'SKILL') return;
      const heal = actor.totalDamageDealt * 0.02;
      applyHealing(actor, Math.min(heal, 80), log, tick, 'Soul Steal');
    },
  },

  // ─── Mganga: Voodoo ───
  'Mganga': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'SKILL') return;
      const ps = actor.heroPassiveState;
      ps.stacks = Math.min((ps.stacks || 0) + 1, 5);
      // DoT per stack
      target.debuffs = target.debuffs.filter(d => d.source !== 'Voodoo');
      target.debuffs.push({
        type: BuffType.DOT,
        name: `Voodoo x${ps.stacks}`,
        source: 'Voodoo',
        damagePerTick: Math.round((27 + (actor.totalStats.magic_power || 0) * 0.05) * ps.stacks / TICKS_PER_SECOND),
        damageType: DamageType.MAGIC,
        tickInterval: 1,
        remainingTicks: 30,
      });
      // -4% magic def per stack
      target.debuffs = target.debuffs.filter(d => d.source !== 'Voodoo Shred');
      target.debuffs.push({
        type: BuffType.STAT_PCT, stat: 'magic_defense', value: 0.04 * ps.stacks,
        name: `Voodoo Shred x${ps.stacks}`, source: 'Voodoo Shred', remainingTicks: 30,
      });
    },
  },

  // ─── Murad: Tectonic Rift ───
  'Murad': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      ps.stacks = Math.min((ps.stacks || 0) + 1, 4);
      if (ps.stacks >= 4) {
        // Ult unlocked
        if (actor.cooldowns['Ultimate']) {
          actor.cooldowns['Ultimate'] = 0;
        }
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Tectonic Rift', detail: 'Ultimate unlocked!' });
      }
    },
  },

  // ─── Nakroth: Dread Judge ───
  'Nakroth': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      ps.stacks = (ps.stacks || 0) + 1;
      if (ps.stacks >= 4) {
        target.debuffs.push({ type: BuffType.CC, ccType: CCType.KNOCKUP, name: 'Dread Judge', source: 'Dread Judge', remainingTicks: 5 });
        ps.stacks = 0;
      }
    },
    onSkillUse(actor) {
      actor.buffs.push({
        type: BuffType.STAT_PCT, stat: 'attack_speed', value: 0.50,
        name: 'Dread Judge AS', source: 'Dread Judge', remainingTicks: 30,
      });
    },
  },

  // ─── Omen: Ferocity ───
  'Omen': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      ps.stacks = (ps.stacks || 0) + 1;
      if (ps.stacks >= 4) {
        const trueDmg = 80 + actor.level * 15;
        target.currentHP = Math.max(0, target.currentHP - trueDmg);
        actor.totalDamageDealt += trueDmg;
        ps.stacks = 0;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Ferocity', detail: `4th hit true ${Math.round(trueDmg)}` });
      }
    },
  },

  // ─── Ormarr: Deterrence ───
  'Ormarr': {
    onHit(actor, target, tick, log, trigger) {
      // Each hit has chance to stun + bonus magic = 4-8% current HP
      if (Math.random() < 0.2) {
        target.debuffs.push({ type: BuffType.CC, ccType: CCType.STUN, name: 'Deterrence', source: 'Deterrence', remainingTicks: 8 });
      }
      const bonusMagic = target.currentHP * (0.04 + actor.level * 0.003);
      target.currentHP = Math.max(0, target.currentHP - bonusMagic);
      actor.totalDamageDealt += bonusMagic;
    },
  },

  // ─── Paine: Soul Divide ───
  'Paine': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'SKILL') return;
      actor.heroPassiveState.customData.enhanced = true;
    },
    onHitAA(actor, target, tick, log) {
      const ps = actor.heroPassiveState;
      if (ps.customData.enhanced) {
        const bonusDmg = actor.totalStats.phys_atk * 0.5;
        target.currentHP = Math.max(0, target.currentHP - bonusDmg);
        actor.totalDamageDealt += bonusDmg;
        ps.customData.enhanced = false;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Soul Divide', detail: `Enhanced AA +${Math.round(bonusDmg)}` });
      }
    },
  },

  // ─── Quillen: Assassin Instinct ───
  'Quillen': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      // Attacks from behind always crit (simplified: 50% chance)
      if (Math.random() < 0.5) {
        const critDmg = actor.totalStats.phys_atk * 1.0;
        target.currentHP = Math.max(0, target.currentHP - critDmg);
        actor.totalDamageDealt += critDmg;
        actor.critCount++;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Backstab Crit', detail: `+${Math.round(critDmg)}` });
      }
    },
  },

  // ─── Ryoma: Naginatajutsu ───
  'Ryoma': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      ps.stacks = (ps.stacks || 0) + 1;
      if (ps.stacks >= 3) {
        const bonusDmg = actor.totalStats.phys_atk * 0.5;
        target.currentHP = Math.max(0, target.currentHP - bonusDmg);
        actor.totalDamageDealt += bonusDmg;
        applyHealing(actor, bonusDmg * 0.3, log, tick, 'Naginatajutsu');
        ps.stacks = 0;
      }
    },
  },

  // ─── Tachi: Shadow Blade ───
  'Tachi': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      const bonusDmg = target.maxHP * 0.02 + actor.totalStats.phys_atk * 0.01;
      target.currentHP = Math.max(0, target.currentHP - bonusDmg);
      actor.totalDamageDealt += bonusDmg;
      
      ps.stacks = Math.min((ps.stacks || 0) + 1, 5);
      if (ps.stacks >= 5) {
        actor.shields.push({ amount: actor.maxHP * 0.25, source: 'Shadow Blade', remainingTicks: 30 });
        ps.stacks = 0;
      }
    },
  },

  // ─── Tel'Annas: Morning Star ───
  "Tel'Annas": {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      target.debuffs = target.debuffs.filter(d => d.source !== 'Morning Star');
      const ps = actor.heroPassiveState;
      ps.stacks = Math.min((ps.stacks || 0) + 1, 5);
      target.debuffs.push({
        type: BuffType.STAT_FLAT, stat: 'phys_defense', value: 10 * ps.stacks,
        name: `Morning Star -Armor x${ps.stacks}`, source: 'Morning Star', remainingTicks: 30,
      });
    },
  },

  // ─── Thane: Royal Power ───
  'Thane': {
    onDamageTaken(defender, attacker, damage, tick, log) {
      if (defender.currentHP / defender.maxHP < 0.3 && defender.heroPassiveState.internalCD <= 0) {
        // 25% DR + heal 24% max HP
        defender.buffs.push({
          type: BuffType.DAMAGE_REDUCTION, value: 0.25,
          name: 'Royal Power DR', source: 'Royal Power', remainingTicks: 30,
        });
        applyHealing(defender, defender.maxHP * 0.24, log, tick, 'Royal Power');
        defender.heroPassiveState.internalCD = 75 * TICKS_PER_SECOND;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: defender.heroName, effect: 'Royal Power', detail: `Shield + Heal ${Math.round(defender.maxHP * 0.24)}` });
      }
    },
  },

  // ─── Tulen: Thunderclap ───
  'Tulen': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'SKILL') return;
      const ps = actor.heroPassiveState;
      ps.stacks = (ps.stacks || 0) + 1;
      if (ps.stacks >= 3) {
        const chainDmg = 150 + (actor.totalStats.magic_power || 0) * 0.35;
        target.currentHP = Math.max(0, target.currentHP - chainDmg);
        actor.totalDamageDealt += chainDmg;
        ps.stacks = 0;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Thunderclap', detail: `Chain Lightning ${Math.round(chainDmg)}` });
      }
    },
  },

  // ─── Valhein: Pocket Glaive ───
  'Valhein': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      ps.stacks = (ps.stacks || 0) + 1;
      if (ps.stacks >= 3) {
        const magicDmg = 80 + actor.level * 12 + (actor.totalStats.magic_power || 0) * 0.3;
        target.currentHP = Math.max(0, target.currentHP - magicDmg);
        actor.totalDamageDealt += magicDmg;
        target.debuffs.push({ type: BuffType.CC, ccType: CCType.STUN, name: 'Pocket Glaive', source: 'Pocket Glaive', remainingTicks: 3 });
        ps.stacks = 0;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Pocket Glaive', detail: `3rd hit stun + magic ${Math.round(magicDmg)}` });
      }
    },
  },

  // ─── Veera: Come Hither ───
  'Veera': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'SKILL') return;
      const ps = actor.heroPassiveState;
      ps.stacks = Math.min((ps.stacks || 0) + 1, 3);
      const shred = (30 + actor.level * 3) * ps.stacks;
      target.debuffs = target.debuffs.filter(d => d.source !== 'Come Hither');
      target.debuffs.push({
        type: BuffType.STAT_FLAT, stat: 'magic_defense', value: shred,
        name: `Come Hither x${ps.stacks}`, source: 'Come Hither', remainingTicks: 30,
      });
    },
  },

  // ─── Violet: Reload ───
  'Violet': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      if (actor.cooldowns['Skill 1'] && actor.cooldowns['Skill 1'] > 0) {
        actor.cooldowns['Skill 1'] = Math.max(0, actor.cooldowns['Skill 1'] - 5); // -0.5s
      }
    },
  },

  // ─── Wukong: God of War ───
  'Wukong': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      ps.stacks = (ps.stacks || 0) + 1;
      if (ps.stacks >= 3) {
        const trueDmg = actor.totalStats.phys_atk * 0.6;
        target.currentHP = Math.max(0, target.currentHP - trueDmg);
        actor.totalDamageDealt += trueDmg;
        ps.stacks = 0;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'God of War', detail: `3rd hit true ${Math.round(trueDmg)}` });
      }
    },
  },

  // ─── Xeniel: Malleus ───
  'Xeniel': {
    onDamageTaken(defender, attacker, damage, tick, log) {
      const ps = defender.heroPassiveState;
      ps.customData.storedDmg = Math.min((ps.customData.storedDmg || 0) + damage, defender.maxHP * 0.06);
    },
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      const stored = ps.customData.storedDmg || 0;
      if (stored > 0) {
        target.currentHP = Math.max(0, target.currentHP - stored);
        actor.totalDamageDealt += stored;
        ps.customData.storedDmg = 0;
      }
    },
  },

  // ─── Yorn: Fierce Shot ───
  'Yorn': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      ps.stacks = (ps.stacks || 0) + 1;
      if (ps.stacks >= 5) {
        const burstDmg = actor.totalStats.phys_atk * 1.2;
        target.currentHP = Math.max(0, target.currentHP - burstDmg);
        actor.totalDamageDealt += burstDmg;
        ps.stacks = 0;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Fierce Shot', detail: `5th hit burst ${Math.round(burstDmg)}` });
      }
    },
  },

  // ─── Zanis: Dragoon ───
  'Zanis': {
    onHit(actor, target, tick, log, trigger) {
      // +12 AD per kill/assist (max 20 stacks = +240 AD)
      // In sim: pre-computed based on assumed stacks
    },
  },

  // ─── Zephys: Unwavering Death ───
  'Zephys': {
    onTick(actor, target, tick, log) {
      const hpLostPct = 1 - (actor.currentHP / actor.maxHP);
      const dr = Math.min(hpLostPct / 0.03 * 0.01, 0.33);
      actor.buffs = actor.buffs.filter(b => b.source !== 'Unwavering Death');
      if (dr > 0) {
        actor.buffs.push({
          type: BuffType.DAMAGE_REDUCTION, value: dr,
          name: `Unwavering Death ${Math.round(dr * 100)}%`, source: 'Unwavering Death', remainingTicks: 15,
        });
      }
    },
  },

  // ─── Zill: Dust Devil ───
  'Zill': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'SKILL') return;
      const ps = actor.heroPassiveState;
      ps.stacks = (ps.stacks || 0) + 1;
      if (ps.stacks >= 3) {
        const bonusDmg = 150 + (actor.totalStats.magic_power || 0) * 0.4;
        target.currentHP = Math.max(0, target.currentHP - bonusDmg);
        actor.totalDamageDealt += bonusDmg;
        ps.stacks = 0;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Dust Devil', detail: `3 marks ${Math.round(bonusDmg)}` });
      }
    },
  },

  // ─── Taara: Fighting Spirit ───
  'Taara': {
    onTick(actor, target, tick, log) {
      const hpLostPct = 1 - (actor.currentHP / actor.maxHP);
      const bonusAD = hpLostPct * actor.totalStats.phys_atk * 0.5;
      actor.buffs = actor.buffs.filter(b => b.source !== 'Fighting Spirit');
      actor.buffs.push({
        type: BuffType.STAT_FLAT, stat: 'phys_atk', value: bonusAD,
        name: 'Fighting Spirit', source: 'Fighting Spirit', remainingTicks: 15,
      });
    },
  },

  // ─── Superman: Flight ───
  'Superman': {
    onTick(actor, target, tick, log) {
      actor.buffs = actor.buffs.filter(b => b.source !== 'Flight');
      actor.buffs.push({
        type: BuffType.STAT_PCT, stat: 'movement_speed', value: 0.30,
        name: 'Flight', source: 'Flight', remainingTicks: 15,
      });
    },
  },

  // ─── Toro: Tough Hide ───
  'Toro': {
    // CC immune during skills - handled in skill execution
    onHit(actor, target, tick, log, trigger) {
      if (trigger === 'SKILL') {
        actor.debuffs = actor.debuffs.filter(d => d.type !== BuffType.CC);
        actor.isStunned = false;
        actor.isSilenced = false;
      }
    },
  },

  // ─── The Flash: Super Speed ───
  'The Flash': {
    onTick(actor, target, tick, log) {
      const ps = actor.heroPassiveState;
      ps.stacks = Math.min((ps.stacks || 0) + 1, 100);
      if (ps.stacks >= 100 && ps.internalCD <= 0) {
        actor.buffs.push({
          type: BuffType.STAT_PCT, stat: 'movement_speed', value: 0.80,
          name: 'Super Speed', source: 'Super Speed', remainingTicks: 10,
        });
        ps.stacks = 0;
        ps.internalCD = 50;
      }
    },
  },

  // ─── Volkath: Dark Power ───
  'Volkath': {
    // On death: revive (similar to Blade of Eternity, handled separately)
    onDamageTaken(defender, attacker, damage, tick, log) {
      if (defender.currentHP <= 0 && !defender.heroPassiveState.customData.revived) {
        defender.currentHP = defender.maxHP * 0.4;
        defender.isAlive = true;
        defender.heroPassiveState.customData.revived = true;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: defender.heroName, effect: 'Dark Power', detail: 'Revive!' });
      }
    },
  },

  // ─── Moren: Tactical Fire ───
  'Moren': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'AA') return;
      const ps = actor.heroPassiveState;
      ps.stacks = (ps.stacks || 0) + 1;
      if (ps.stacks >= 4) {
        const bonusDmg = actor.totalStats.phys_atk * 0.5;
        target.currentHP = Math.max(0, target.currentHP - bonusDmg);
        actor.totalDamageDealt += bonusDmg;
        applyHealing(actor, bonusDmg * 0.3, log, tick, 'Tactical Fire');
        ps.stacks = 0;
      }
    },
  },

  // ─── Mina: Sweet Revenge ───
  'Mina': {
    onHit(actor, target, tick, log, trigger) {
      const ps = actor.heroPassiveState;
      ps.stacks = Math.min((ps.stacks || 0) + 1, 10);
      actor.buffs = actor.buffs.filter(b => b.source !== 'Sweet Revenge');
      actor.buffs.push({
        type: BuffType.STAT_PCT, stat: 'movement_speed', value: 0.02 * ps.stacks,
        name: `Sweet Revenge x${ps.stacks}`, source: 'Sweet Revenge', remainingTicks: 30,
      });
    },
    onDamageTaken(defender, attacker, damage, tick, log) {
      const ps = defender.heroPassiveState;
      ps.stacks = Math.min((ps.stacks || 0) + 1, 10);
    },
  },

  // ─── Wiro: Iron Body ───
  'Wiro': {
    onTick(actor, target, tick, log) {
      if (actor.currentHP / actor.maxHP < 0.4) {
        actor.buffs = actor.buffs.filter(b => b.source !== 'Iron Body');
        actor.buffs.push({
          type: BuffType.DAMAGE_REDUCTION, value: 0.15,
          name: 'Iron Body', source: 'Iron Body', remainingTicks: 15,
        });
      }
    },
  },

  // ─── Brunhilda: Valkyrie's Might ───
  'Brunhilda': {
    onTick(actor, target, tick, log) {
      if (actor.currentHP / actor.maxHP < 0.5) {
        actor.buffs = actor.buffs.filter(b => b.source !== "Valkyrie's Might");
        actor.buffs.push({
          type: BuffType.STAT_FLAT, stat: 'phys_defense', value: 80,
          name: "Valkyrie's Might Armor", source: "Valkyrie's Might", remainingTicks: 15,
        });
        actor.buffs.push({
          type: BuffType.STAT_FLAT, stat: 'magic_defense', value: 80,
          name: "Valkyrie's Might MR", source: "Valkyrie's Might MR", remainingTicks: 15,
        });
      }
    },
  },

  // ─── Kil'Groth: Oceanic Blessing ───
  "Kil'Groth": {
    onTick(actor, target, tick, log) {
      if (tick % 20 !== 0) return;
      const hpPct = actor.currentHP / actor.maxHP;
      const regenPct = hpPct < 0.5 ? 0.02 : 0.005;
      applyHealing(actor, actor.maxHP * regenPct, log, tick, 'Oceanic Blessing');
    },
  },

  // ─── D'Arcy: Dimensional Walk ───
  "D'Arcy": {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'SKILL') return;
      actor.heroPassiveState.customData.enhanced = true;
    },
  },

  // ─── Skud: Anabolic Rage ───
  'Skud': {
    onTick(actor, target, tick, log) {
      if (actor.currentHP / actor.maxHP < 0.5) {
        actor.buffs = actor.buffs.filter(b => b.source !== 'Anabolic Rage');
        actor.buffs.push({ type: BuffType.STAT_PCT, stat: 'attack_speed', value: 0.15, name: 'Anabolic AS', source: 'Anabolic Rage', remainingTicks: 15 });
        actor.buffs.push({ type: BuffType.STAT_FLAT, stat: 'life_steal', value: 0.10, name: 'Anabolic LS', source: 'Anabolic Rage LS', remainingTicks: 15 });
      }
    },
  },

  // ─── Wonder Woman: Amazon Shield ───
  'Wonder Woman': {
    onDamageTaken(defender, attacker, damage, tick, log) {
      const bonusAD = damage * 0.05;
      defender.buffs.push({
        type: BuffType.STAT_FLAT, stat: 'phys_atk', value: Math.min(bonusAD, 50),
        name: 'Amazon Shield', source: 'Amazon Shield', remainingTicks: 30,
      });
    },
  },

  // ─── Natalya: Arcane Nova ───
  'Natalya': {
    onHit(actor, target, tick, log, trigger) {
      if (trigger !== 'SKILL') return;
      const ps = actor.heroPassiveState;
      ps.stacks = (ps.stacks || 0) + 1;
      if (ps.stacks >= 5) {
        const bonusDmg = 200 + (actor.totalStats.magic_power || 0) * 0.5;
        target.currentHP = Math.max(0, target.currentHP - bonusDmg);
        actor.totalDamageDealt += bonusDmg;
        ps.stacks = 0;
        log.addEvent(tick, 'HERO_PASSIVE', { hero: actor.heroName, effect: 'Arcane Nova', detail: `5th ability +${Math.round(bonusDmg)}` });
      }
    },
  },
};

// ============================================================================
// GENERIC PASSIVES (for heroes not explicitly defined above)
// ============================================================================

// Heroes with simple/no combat-relevant passives
const SIMPLE_PASSIVE_HEROES = [
  'Aya', 'Billow', 'Biron', 'Bolt Baron', 'Bonnie', 'Bright', 'Charlotte',
  'Chaugnar', 'Cresht', 'Dextra', 'Dirak', 'Dolia', 'Erin', 'Heino',
  'Flowborn Carry', 'Flowborn Mage', 'Helen', 'Iggy', 'Ignis', 'Ishar', 'Jinnar', 'Kahlii', 'Kaine',
  'Keera', 'Krixi', 'Krizzix', 'Liliana', 'Lindis', 'Lorion', 'Lumburr',
  'Max', 'Ming', 'Omega', 'Preyta', 'Qi', 'Raz', 'Riktor', 'Rouie',
  'Rourke', 'Roxie', 'Sephera', 'Sinestrea', 'Slimz', 'Stuart',
  'Teeri', 'TeeMee', 'Thorne', 'Veres', 'Wisp', 'Yan', 'Yena',
  'Yue', 'Zata', 'Zip', 'Zuka',
];

// ============================================================================
// REGISTER ALL PASSIVES
// ============================================================================

export function registerAllHeroPassives() {
  // Register explicit passives
  for (const [heroName, handler] of Object.entries(HERO_PASSIVES)) {
    registerHeroPassive(heroName, handler);
  }
  
  // Register empty passives for simple heroes
  for (const heroName of SIMPLE_PASSIVE_HEROES) {
    registerHeroPassive(heroName, {
      onHit() {},
      onTick() {},
      onDamageTaken() {},
    });
  }
  
  console.log(`[HeroPassives] Registered ${Object.keys(HERO_PASSIVES).length} active + ${SIMPLE_PASSIVE_HEROES.length} simple = ${Object.keys(HERO_PASSIVES).length + SIMPLE_PASSIVE_HEROES.length} total hero passives`);
}

// ============================================================================
// HELPER: Get passive info for display
// ============================================================================

export function getHeroPassiveInfo(heroName) {
  const handler = HERO_PASSIVES[heroName];
  if (handler) {
    return {
      heroName,
      hasActivePassive: true,
      type: 'ACTIVE',
    };
  }
  return {
    heroName,
    hasActivePassive: false,
    type: 'SIMPLE',
  };
}

export function getActivePassiveCount() {
  return Object.keys(HERO_PASSIVES).length;
}

export function getSimplePassiveCount() {
  return SIMPLE_PASSIVE_HEROES.length;
}
